import React from 'react';
import { Button } from '../../../components/ui/button';
import { PlanType, BillingCycle } from '../../../types';
import { PLAN_INFO } from '../../../config/constants';
import { formatCurrency } from '../../../lib/utils';

interface PlanCardProps {
  plan: PlanType;
  billingCycle: BillingCycle;
  currentPlan?: PlanType;
  isLoading?: boolean;
  onSelect: () => void;
}

const PLAN_SUBTITLES: Record<string, string> = {
  starter: '小規模サイト向け',
  pro: '成長中のビジネス向け',
  business: '大規模・複数拠点向け',
};

function buildFeatures(plan: 'starter' | 'pro' | 'business'): string[] {
  const info = PLAN_INFO[plan];
  const storageText = info.storage_mb >= 1024 ? `${info.storage_mb / 1024}GB` : `${info.storage_mb}MB`;
  const base = [
    `チャットボット${info.domains}個`,
    `月${info.monthly_messages.toLocaleString()}メッセージ`,
    `履歴${info.chat_history_days}日`,
    `ストレージ${storageText}`,
  ];
  if (plan === 'starter') return [...base, '標準サポート'];
  if (plan === 'pro') return [...base, 'リード管理・CSVエクスポート', '詳細分析', '優先サポート'];
  return [...base, 'Proの全機能', '優先サポート'];
}

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a7a72" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingCycle,
  currentPlan,
  isLoading = false,
  onSelect,
}) => {
  const info = PLAN_INFO[plan];
  const isCurrentPlan = currentPlan === plan;
  const isPopular = plan === 'pro';

  const price = billingCycle === 'yearly' ? info.price_yearly : info.price_monthly;
  const monthlyPrice = billingCycle === 'yearly' ? Math.round(price / 12) : price;
  const isFree = monthlyPrice === 0;

  const planOrder = ['trial', 'starter', 'pro', 'business'];

  const getButtonText = () => {
    if (isCurrentPlan) return '現在のプラン';
    if (!currentPlan || currentPlan === 'trial') return '選択する';
    const currentIndex = planOrder.indexOf(currentPlan);
    const targetIndex = planOrder.indexOf(plan);
    return targetIndex > currentIndex ? 'アップグレード' : 'ダウングレード';
  };

  const features = (plan === 'starter' || plan === 'pro' || plan === 'business')
    ? buildFeatures(plan)
    : info.features;

  // testid: plan-card-starter | plan-card-pro | plan-card-business
  return (
    <div
      data-testid={`plan-card-${plan}`}
      className={`relative h-full flex flex-col rounded-lg ${
        isCurrentPlan
          ? 'border-2 border-primary'
          : isPopular
          ? 'border shadow-lg'
          : 'border'
      }`}
    >
      {isPopular && (
        <span className="absolute -top-3 right-4 text-white text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#0a7a72' }}>
          おすすめ
        </span>
      )}

      <div className="p-6 pb-0">
        <h3 className="text-lg font-semibold">{info.name}</h3>
        {PLAN_SUBTITLES[plan] && (
          <p className="text-xs text-gray-500 mt-0.5">{PLAN_SUBTITLES[plan]}</p>
        )}
        <div className="mt-2">
          <span className="text-3xl font-bold">
            {isFree ? '¥0' : formatCurrency(monthlyPrice)}
          </span>
          {!isFree && (
            <span className="text-sm text-gray-500">/月</span>
          )}
          {billingCycle === 'yearly' && !isFree && (
            <p className="text-xs text-gray-500 mt-1">
              年額 {formatCurrency(price)}（2ヶ月分お得）
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 p-6 pt-4 flex flex-col">
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              <CheckIcon />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-6">
          <Button
            className="w-full"
            variant={isCurrentPlan ? 'outline' : 'default'}
            disabled={isCurrentPlan || isLoading}
            onClick={onSelect}
            data-testid={`plan-upgrade-button-${plan}`}
          >
            {isLoading ? '処理中...' : getButtonText()}
          </Button>
        </div>
      </div>
    </div>
  );
};
